import { createContext, useContext } from 'react';
import { PossibleSectionInDependencies } from './types';
import { ActiveState } from '../../building-blocks/activity-state/helpers';

export interface SearchResult {
  sourceText?: string; // The full source text that was searched (to distinguish between different elements)
  section: PossibleSectionInDependencies;
  tableType?: string;
  rowIndex?: number;
  rowId?: string; // The ID of the row item for precise identification
  field: string;
  matchIndex: number; // Global match index across all results
  occurrenceIndex: number; // Local occurrence index within the same element (0-based)
}

export interface SectionExpansionState {
  details: boolean;
  dependencies: boolean;
  usedBy: boolean;
  configurations: boolean;
  latestDeployed: boolean;
  tags: boolean;
  openAi: boolean;
  sourceCode: boolean;
}

export interface SectionActiveStates {
  dependsOnActiveState?: ActiveState;
  usedByActiveState?: ActiveState;
  configurationsActiveState?: ActiveState;
}

export interface SearchContextType {
  isInputVisible: boolean;
  searchTerm: string;
  arrowNavigationMatchIdx: number;
  totalMatches: number;
  searchResults: SearchResult[];
  searchResultsForTerm: string; // Track which search term the results are for
  sectionExpansion: SectionExpansionState;
  isSearching: boolean;
  setIsInputVisible: (isInputVisible: boolean) => void;
  onSearch: (term: string) => void;
  onNext: () => void;
  onPrevious: () => void;
  onClear: () => void;
  scrollToMatch: (matchIndex: number) => void;
  setSectionExpansion: (section: keyof SectionExpansionState, expanded: boolean) => void;
  hasTableResults: (section: SearchResult['section'], tableType: string) => boolean;
}

const SearchContext = createContext<SearchContextType | null>(null);

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within SearchProvider');
  }
  return context;
};

export { SearchContext };
